import { StreamLanguage, type StreamParser } from '@codemirror/language';

// True control-flow / declaration keywords — stay purple (--tk-k)
const KEYWORDS = new Set([
  'module', 'function', 'if', 'else', 'for', 'let', 'each', 'include', 'use',
  'children', 'true', 'false', 'undef', 'import',
]);

// Module/function calls — rendered as function names (amber accent, --tk-f)
const MODULES = new Set([
  'cube', 'sphere', 'cylinder', 'polyhedron',
  'union', 'difference', 'intersection',
  'translate', 'rotate', 'scale', 'mirror', 'resize', 'offset', 'hull', 'minkowski',
  'linear_extrude', 'rotate_extrude', 'tapered_extrude',
  'chamfer', 'fillet',
  'circle', 'square', 'polygon',
  'align', 'attach', 'position', 'group', 'frame', 'color',
  'chamfered_cylinder',
  'spur_gear', 'helical_gear', 'herringbone_gear', 'rack', 'bevel_gear', 'worm',
  'l_profile', 'u_profile', 'o_profile',
  'torus', 'pipe', 'ellipsoid', 'wedge',
  'capsule', 'rounded_cube',
  'regular_polygon', 'hexagon', 'slot', 'star', 'text',
  't_profile', 'i_profile',
  'thread', 'spring',
  'bolt', 'nut', 'standoff',
  'enclosure', 'shell',
  'hole_pattern', 'bolt_hole', 'nut_hole',
]);

// Math + utility built-in functions — also amber
const BUILTINS = new Set([
  'sin', 'cos', 'tan', 'abs', 'ceil', 'floor', 'round', 'sqrt', 'pow',
  'min', 'max', 'ln', 'log',
  'len', 'concat',
]);

interface ScadState {
  inBlockComment: boolean;
  parenDepth: number;
}

const scadParser: StreamParser<ScadState> = {
  startState(): ScadState {
    return { inBlockComment: false, parenDepth: 0 };
  },
  copyState(s) {
    return { inBlockComment: s.inBlockComment, parenDepth: s.parenDepth };
  },

  token(stream, state): string | null {
    // Continue block comment
    if (state.inBlockComment) {
      while (!stream.eol()) {
        if (stream.match('*/')) {
          state.inBlockComment = false;
          return 'blockComment';
        }
        stream.next();
      }
      return 'blockComment';
    }

    // Line comment
    if (stream.match('//')) {
      stream.skipToEnd();
      return 'lineComment';
    }

    // Block comment start
    if (stream.match('/*')) {
      state.inBlockComment = true;
      while (!stream.eol()) {
        if (stream.match('*/')) {
          state.inBlockComment = false;
          return 'blockComment';
        }
        stream.next();
      }
      return 'blockComment';
    }

    // String
    if (stream.match('"')) {
      while (!stream.eol()) {
        const ch = stream.next();
        if (ch === '\\') {
          stream.next();
        } else if (ch === '"') {
          return 'string';
        }
      }
      return 'string';
    }

    // Number
    if (stream.match(/^[0-9]*\.?[0-9]+([eE][+-]?[0-9]+)?/)) {
      return 'number';
    }

    // Special variables ($fn, $fa, $fs)
    if (stream.match(/^\$[a-zA-Z_][a-zA-Z0-9_]*/)) {
      return 'variableName.special';
    }

    // Identifiers and keywords
    if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)) {
      const word = stream.current();
      if (KEYWORDS.has(word)) return 'keyword';
      if (MODULES.has(word) || BUILTINS.has(word)) return 'variableName.function';
      // Any identifier followed by `(` is a call (user-defined module or
      // function) — render in the same amber as built-in modules.
      if (stream.match(/^\s*\(/, false)) return 'variableName.function';
      // Named argument inside a CALL (paren depth > 0) — the identifier
      // sits on the left of `=` (not `==`). Top-level assignments like
      // `width = 40;` stay as plain variables.
      if (state.parenDepth > 0 && stream.match(/^\s*=(?!=)/, false)) {
        return 'propertyName';
      }
      return 'variableName';
    }

    // Debug modifier #
    if (stream.match('#')) {
      return 'keyword';
    }

    // Operators
    if (stream.match(/^[+\-*\/%^<>=!&|]+/)) {
      return 'operator';
    }

    // Brackets and punctuation — track paren depth so we can detect
    // named-call args (identifier=value) vs top-level assignments.
    if (stream.match('(')) { state.parenDepth++; return 'punctuation'; }
    if (stream.match(')')) { if (state.parenDepth > 0) state.parenDepth--; return 'punctuation'; }
    if (stream.match(/^[{}[\];,:]/)) {
      return 'punctuation';
    }

    // Skip whitespace
    if (stream.eatSpace()) return null;

    // Skip unknown characters
    stream.next();
    return null;
  },
};

export const scadLanguage = StreamLanguage.define(scadParser);
